import React, { useCallback, useState } from 'react';

import type { UUID } from '@aline/common';
import { Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { RotateCcw } from 'lucide-react';
import type { AvatarInfo } from 'src/components';
import { AlineAvatar, AlineButton, AlineTooltip } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { getPermissionAssigneeDisplayDetails, getPermissionAvatarInfo } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import { getReReviewCandidates, getReReviewMessage } from './task-review-helpers';
import { TaskCommentDialog } from './TaskCommentDialog';
import { useTaskReviewActions } from './use-task-review-actions';

const useStyles = makeStyles()(theme => ({
  checkbox: {
    padding: theme.spacing(0.5, 1),
  },
  // Grows with the other footer buttons but never below its label, the longest of them.
  grow: {
    flex: '1 0 auto',
  },
  reviewer: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  reviewerName: {
    color: theme.appColors.strongType,
    fontSize: 14,
    fontWeight: 600,
  },
  reviewerState: {
    color: theme.appColors.iconsType,
    fontSize: 12,
  },
}));

const RE_REVIEW_STATE_TEXT = {
  [Apollo.TaskAssigneeDecision.ChangesRequested]: 'Requested changes',
  [Apollo.TaskAssigneeDecision.Completed]: 'Approved',
  [Apollo.TaskAssigneeDecision.Rejected]: 'Rejected',
} satisfies Record<Apollo.TaskAssigneeDecision, string>;

export interface ReReviewReviewer {
  avatarColor: string | undefined;
  avatarInfo: AvatarInfo;
  /** Ticked by default: the reviewer requested changes. */
  isDefault: boolean;
  name: string;
  permissionId: UUID;
  /** "Requested changes" or "Approved", under the name in the list. */
  stateText: string;
}

interface RequestReReviewButtonProps {
  disabledReason: string | null;
  isPrimary: boolean;
  /** The reviewers the assignee can ask again, none of them reviewing right now. */
  reviewers: readonly ReReviewReviewer[];
  taskId: UUID;
  className?: string;
  /** Sizes the button to its label, e.g. in the task page bar; the side panel stretches it. */
  isBar?: boolean;
}

/**
 * The assignee's way back to review after changes were requested. With more than one reviewer to
 * choose from, the dialog lists them, ticking those who requested changes, and asks the ticked
 * ones in one request.
 */
export const RequestReReviewButton = React.memo(function RequestReReviewButton({
  className,
  disabledReason,
  isBar = false,
  isPrimary,
  reviewers,
  taskId,
}: RequestReReviewButtonProps) {
  const { classes } = useStyles();
  const actions = useTaskReviewActions(taskId);
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<UUID>>(() => new Set());
  const hasChoice = reviewers.length > 1;
  const selectedNames = reviewers
    .filter(reviewer => selectedIds.has(reviewer.permissionId))
    .map(reviewer => reviewer.name);

  const openDialog = useCallback(() => {
    const ids = hasChoice ? reviewers.filter(reviewer => reviewer.isDefault) : reviewers;
    setSelectedIds(new Set(ids.map(reviewer => reviewer.permissionId)));
    setOpen(true);
  }, [hasChoice, reviewers]);
  const toggleSelected = useCallback((permissionId: UUID) => {
    setSelectedIds(previous => {
      const next = new Set(previous);
      if (next.has(permissionId)) next.delete(permissionId);
      else next.add(permissionId);
      return next;
    });
  }, []);

  return (
    <>
      <AlineTooltip arrow placement="top" title={disabledReason ?? ''}>
        <div className={isBar ? undefined : classes.grow}>
          <AlineButton
            className={className}
            color="primary"
            disabled={Boolean(disabledReason)}
            fullWidth={!isBar}
            startIcon={<RotateCcw size={16} />}
            variant={isPrimary ? 'contained' : 'outlined'}
            onClick={openDialog}
          >
            Request Re-Review
          </AlineButton>
        </div>
      </AlineTooltip>
      <TaskCommentDialog
        isSubmitDisabled={!selectedIds.size}
        label="Note"
        message={
          hasChoice
            ? 'Choose who to ask again. Asking starts the next round.'
            : getReReviewMessage(selectedNames)
        }
        open={open}
        placeholder="Type a note for the reviewers..."
        primaryText="Request Re-Review"
        title="Request Re-Review"
        onClose={() => setOpen(false)}
        onSubmit={note => actions.requestReview({ note, reviewerPermissionIds: [...selectedIds] })}
      >
        {hasChoice && (
          <FormGroup>
            {reviewers.map(reviewer => (
              <FormControlLabel
                key={reviewer.permissionId}
                control={
                  <Checkbox
                    checked={selectedIds.has(reviewer.permissionId)}
                    className={classes.checkbox}
                    size="small"
                    onChange={() => toggleSelected(reviewer.permissionId)}
                  />
                }
                label={
                  <div className={classes.reviewer}>
                    <AlineAvatar
                      avatarInfo={reviewer.avatarInfo}
                      styles={{ backgroundColor: reviewer.avatarColor, size: 'sm' }}
                    />
                    <div>
                      <Typography className={classes.reviewerName}>{reviewer.name}</Typography>
                      <Typography className={classes.reviewerState}>
                        {reviewer.stateText}
                      </Typography>
                    </div>
                  </div>
                }
              />
            ))}
          </FormGroup>
        )}
      </TaskCommentDialog>
    </>
  );
});

/** Who the assignee can ask again, ticking by default the reviewers who requested changes. */
export function getReReviewReviewers(
  assignments: Apollo.TaskReviewFieldsFragment['assignments']
): ReReviewReviewer[] {
  return getReReviewCandidates(assignments).map(({ assignment, isDefault }) => {
    const { permission } = assignment;
    const displayDetails = getPermissionAssigneeDisplayDetails(permission);
    return {
      avatarColor: displayDetails.avatarColor,
      avatarInfo: getPermissionAvatarInfo(permission),
      isDefault,
      name: displayDetails.primary,
      permissionId: permission.id,
      stateText: assignment.decision ? RE_REVIEW_STATE_TEXT[assignment.decision] : 'Not asked yet',
    };
  });
}
